import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { PencilIcon, Eye, EyeOff } from "lucide-react";
import apiClientChoice from "../../../utils/apiClientChoice";
import toast from "react-hot-toast";

const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "DEACTIVATED", label: "Deactivated" },
  { value: "DUMMY", label: "Dummy" },
];

export function EditMOSLUserModal({ userDetails, fetchUserList, forceOpen = false, onForceClose }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [userData, setUserData] = useState({
    client_id: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    pin: "",
    dob: "",
    api_key: "",
    api_secret_key: "",
    totp_key: "",
    vendor_id: "",
    vendor_key: "",
    encryption_key: "",
    encryption_iv: "",
    api_key_expiry_date: "",
    session_id: "",
    account_status: "ACTIVE",
    active: false,
    broker: "MOSL",
    multiplier: "0",
    sizing: 0,
    type: "",
    proxy_user: "",
    proxy_password: "",
    proxy_host: "",
    proxy_host_db: "",
    proxy_port: 0,
    proxy_static_ip: "",
    proxy_toggle: false,
  });

  const [visibleFields, setVisibleFields] = useState({});

  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const secretFields = ["password", "api_key", "api_secret_key", "totp_key", "proxy_password"];

  const fetchUserDetails = async () => {
    try {
      const response = await apiClientChoice.get("https://choice.server.sypto.xyz/users");
      const allUsers = response.data || [];
      const found = allUsers.find(
        (u) => u.client_id === userDetails.client_id && u.broker === "MOSL"
      );
      if (found) {
        setUserData({
          client_id: found.client_id || "",
          name: found.name || "",
          email: found.email || "",
          phone: found.phone || "",
          password: found.password || "",
          pin: found.pin || "",
          dob: found.dob || "",
          api_key: found.api_key || "",
          api_secret_key: found.api_secret_key || "",
          totp_key: found.totp_key || "",
          vendor_id: found.vendor_id || "",
          vendor_key: found.vendor_key || "",
          encryption_key: found.encryption_key || "",
          encryption_iv: found.encryption_iv || "",
          api_key_expiry_date: found.api_key_expiry_date || "",
          session_id: found.session_id || "",
          account_status: found.account_status || "ACTIVE",
          active: found.active || false,
          broker: found.broker || "MOSL",
          multiplier: found.multiplier !== undefined ? String(found.multiplier) : "0",
          sizing: found.sizing || 0,
          type: found.type || "",
          proxy_user: found.proxy_user || "",
          proxy_password: found.proxy_password || "",
          proxy_host: found.proxy_host || "",
          proxy_host_db: found.proxy_host_db || "",
          proxy_port: found.proxy_port || 0,
          proxy_static_ip: found.proxy_static_ip || "",
          proxy_toggle: found.proxy_toggle || false,
        });
      } else {
        toast.error("MOSL user not found on Choice server");
      }
    } catch (error) {
      console.error("Failed to fetch MOSL user details:", error);
      toast.error("Failed to load user details");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = async () => {
    setIsSaving(true);
    const payload = {
      client_id: userData.client_id,
      name: userData.name,
      email: userData.email,
      phone: userData.phone,
      password: userData.password,
      pin: userData.pin,
      dob: userData.dob,
      api_key: userData.api_key,
      api_secret_key: userData.api_secret_key,
      totp_key: userData.totp_key,
      vendor_id: userData.vendor_id,
      vendor_key: userData.vendor_key,
      encryption_key: userData.encryption_key,
      encryption_iv: userData.encryption_iv,
      api_key_expiry_date: userData.api_key_expiry_date,
      session_id: userData.session_id,
      account_status: userData.account_status,
      active: userData.active,
      broker: userData.broker,
      multiplier: userData.multiplier,
      sizing: parseFloat(userData.sizing) || 0,
      type: userData.type,
      proxy_user: userData.proxy_user,
      proxy_password: userData.proxy_password,
      proxy_host: userData.proxy_host,
      proxy_host_db: userData.proxy_host_db,
      proxy_port: parseInt(userData.proxy_port) || 0,
      proxy_static_ip: userData.proxy_static_ip,
      proxy_toggle: userData.proxy_toggle,
    };

    try {
      await apiClientChoice.request({
        method: "put",
        url: `https://choice.server.sypto.xyz/update/user/${userData.client_id}`,
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(payload),
      });
      toast.success("MOSL user updated successfully!", { duration: 5000 });
      if (fetchUserList) fetchUserList();
      setOpen(false);
      if (onForceClose) onForceClose();
    } catch (error) {
      console.error("Failed to update MOSL user:", error);
      toast.error("Failed to update user");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenModal = () => {
    setOpen(true);
    fetchUserDetails();
  };

  useEffect(() => {
    if (forceOpen) {
      handleOpenModal();
    }
  }, [forceOpen]);

  const textFields = [
    { key: "client_id", label: "Client ID", readOnly: true },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "dob", label: "DOB (2FA)" },
    { key: "password", label: "Password" },
    { key: "api_key", label: "API Key" },
    { key: "api_secret_key", label: "API Secret Key" },
    { key: "totp_key", label: "TOTP Key" },
    { key: "sizing", label: "Sizing", type: "number" },
    { key: "proxy_user", label: "Proxy User" },
    { key: "proxy_password", label: "Proxy Password" },
    { key: "proxy_host", label: "Proxy Host" },
    { key: "proxy_port", label: "Proxy Port", type: "number" },
    { key: "proxy_static_ip", label: "Proxy Static IP" },
  ];

  return (
    <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val && onForceClose) onForceClose(); }}>
      {!forceOpen && (
        <DialogTrigger asChild>
          <Button
            onClick={handleOpenModal}
            variant="outline"
            className="text-[12px] h-[25px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
          >
            <PencilIcon className="mr-2 w-3 h-3 text-gray-500" />
            Edit User
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[1025px] border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit MOSL User — {userData.client_id}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-4 py-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4">
          {textFields.map(({ key, label, type, readOnly }) => (
            <div key={key} className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">{label}</Label>
              <div className="relative">
                <Input
                  className={`text-white pr-10 ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                  type={
                    secretFields.includes(key) && !visibleFields[key]
                      ? "password"
                      : type || "text"
                  }
                  name={key}
                  value={userData[key]}
                  onChange={readOnly ? undefined : handleChange}
                  readOnly={readOnly}
                />
                {secretFields.includes(key) && (
                  <button
                    type="button"
                    onClick={() => toggleVisibility(key)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                  >
                    {visibleFields[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Status Dropdown */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Account Status</Label>
            <Select
              value={userData.account_status}
              onValueChange={(val) => setUserData((prev) => ({ ...prev, account_status: val }))}
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Proxy Toggle */}
          <div className="flex items-center space-x-2 h-full pt-8">
            <Checkbox
              id="mosl-edit-proxy-toggle"
              checked={userData.proxy_toggle}
              onCheckedChange={(checked) =>
                setUserData((prev) => ({ ...prev, proxy_toggle: checked }))
              }
            />
            <Label htmlFor="mosl-edit-proxy-toggle" className="text-white cursor-pointer">
              Proxy Toggle
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            disabled={isSaving}
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
            onClick={handleUpdateUser}
          >
            {isSaving ? "Saving…" : "Update User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
